import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Empty, PageHeader, Pager, Td, Th } from "../components/common/UI";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function AuditLogs() {
  const [f, setF] = useState({ q: "", action: "", actor: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [inspect, setInspect] = useState(null);
  const params = useCallback(() => Object.fromEntries(Object.entries(f).filter(([, v]) => v)), [f]);
  const load = useCallback(() => {
    api.get("/audit-logs", { params: { ...params(), page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [params, page]);
  useEffect(() => { load(); }, [load]);
  const up = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setPage(1); };
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Compliance" title="Audit Logs" subtitle="Append-only record of system actions. Database triggers block updates and deletions of audit entries."
        actions={<Button size="sm" variant="outline" onClick={() => downloadFile("/audit-logs/export", params(), "otms-audit.csv").catch((e) => toast.error(errMsg(e)))} data-testid="audit-export-button"><Download className="mr-1.5 h-4 w-4" />Export CSV</Button>} />
      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-5">
        <input className="otms-input" placeholder="Search summary / record ID" value={f.q} onChange={up("q")} data-testid="audit-search-input" />
        <select className="otms-input" value={f.action} onChange={up("action")} data-testid="audit-action-filter">
          <option value="">All actions</option>
          {["auth", "measure", "workflow", "numbering", "document", "user", "role", "config", "lgu", "task", "export"].map((a) => <option key={a} value={a}>{a}.*</option>)}
        </select>
        <input className="otms-input" placeholder="Actor email" value={f.actor} onChange={up("actor")} data-testid="audit-actor-input" />
        <input type="date" className="otms-input" value={f.date_from} onChange={up("date_from")} data-testid="audit-date-from" />
        <input type="date" className="otms-input" value={f.date_to} onChange={up("date_to")} data-testid="audit-date-to" />
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px]" data-testid="audit-table">
          <thead><tr><Th>#</Th><Th>When (Asia/Manila)</Th><Th>Actor</Th><Th>Action</Th><Th>Summary</Th><Th /></tr></thead>
          <tbody>
            {data?.items.map((a) => (
              <tr key={a.id}>
                <Td className="font-mono text-[10px] text-slate-400">{a.id}</Td>
                <Td>{fmtDateTime(a.created_at)}</Td>
                <Td>{a.actor_email || "system"}</Td>
                <Td><span className="font-mono text-[11px] text-sky-700">{a.action}</span></Td>
                <Td className="max-w-md">{a.summary}</Td>
                <Td>{a.changes && <button className="text-[11px] text-sky-700 hover:underline" onClick={() => setInspect(a)} data-testid={`audit-inspect-${a.id}`}>Details</button>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <Empty />}
      </div>
      <Pager meta={data} onPage={setPage} />
      <Dialog open={!!inspect} onOpenChange={(o) => !o && setInspect(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Change details #{inspect?.id}</DialogTitle></DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded bg-slate-900 p-4 font-mono text-[11px] text-sky-100" data-testid="audit-json">{JSON.stringify(inspect?.changes, null, 2)}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
